import { useMemo, useState } from "react";
import { MapPin, Phone, Mail, Inbox, Calendar, Clock, Pencil, KeyRound, UserX, UserCheck, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Form, Input, Modal, Button } from "../../components/ui";
import { useSelector } from "react-redux";
import swal from "../../utils/swal";
import { TEACHERS, UPLOADS_URL } from "../../config/constants";
import { Post } from "../../config/api/post";
import { generateTeacherUpdatePassword } from "../../config/helpers";
import { useGetClassTimetables } from "../../query/classQuery";
import { useUpdateTeacherStatus } from "../../query/teacherQuery";
import { DetailCoverHero } from "../../components/DetailCoverHero";
import "./teacherDetails.css";

const WEEKDAYS = [
  { key: "MON", label: "Mon" },
  { key: "TUE", label: "Tue" },
  { key: "WED", label: "Wed" },
  { key: "THU", label: "Thu" },
  { key: "FRI", label: "Fri" },
];

const DAY_LABELS = {
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
};

const LESSON_TONES = ["is-navy", "is-green", "is-yellow", "is-blue"];

function initials(firstName = "", lastName = "") {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "T";
}

function formatRange(start, end) {
  if (!start && !end) return "";
  const from = start ? dayjs(start).format("YYYY") : "";
  const to = end ? dayjs(end).format("YYYY") : "";
  return [from, to].filter(Boolean).join(" - ");
}

function classroomIdOf(teacher) {
  const room = teacher?.classroom;
  if (!room) return "";
  if (typeof room === "string") return room;
  return String(room._id || room.id || "");
}

function todayDayKey() {
  const map = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const key = map[dayjs().day()];
  return WEEKDAYS.some((day) => day.key === key) ? key : "MON";
}

function formatSlotTime(start, end) {
  const parse = (value) => {
    if (!value) return "";
    const parsed = dayjs(`2000-01-01 ${value}`);
    return parsed.isValid() ? parsed.format("hh:mm A") : value;
  };
  const from = parse(start);
  const to = parse(end);
  if (from && to) return `${from} - ${to}`;
  return from || to || "Time not set";
}

export default function TeacherProfile({ teacher, onDelete }) {
  const navigate = useNavigate();
  const token = useSelector((state) => state.user.userToken);
  const classroomId = classroomIdOf(teacher);
  const [selectedDay, setSelectedDay] = useState(todayDayKey);
  const [resetOpen, setResetOpen] = useState(false);
  const [form] = Form.useForm();
  const { data: timetable = {}, isLoading } = useGetClassTimetables(classroomId);
  const { mutate: updateTeacherStatus, isPending: isUpdatingStatus } = useUpdateTeacherStatus();

  const slots = useMemo(() => {
    const list = Array.isArray(timetable?.[selectedDay]) ? timetable[selectedDay] : [];
    return [...list].sort((a, b) => String(a.startTime || "").localeCompare(String(b.startTime || "")));
  }, [timetable, selectedDay]);

  const fullName = `${teacher?.firstName || ""} ${teacher?.lastName || ""}`.trim() || "Teacher";
  const locationLabel = [teacher?.city, teacher?.state || (teacher?.city ? "Indonesia" : "")]
    .filter(Boolean)
    .join(", ");
  const education = teacher?.education || [];
  const expertise = education
    .flatMap((item) => item.subject || [])
    .filter(Boolean);
  const className = teacher?.classroom?.classroomName;
  const role = className ? `${className} Teacher` : "Teacher";
  const isActive = teacher?.status === "ACTIVE";
  const statusLabel = teacher?.status === "ACTIVE"
    ? "Active"
    : teacher?.status === "PENDING"
      ? "Pending"
      : "Inactive";

  const handleStatus = () => {
    const nextStatus = isActive ? "INACTIVE" : "ACTIVE";
    swal({
      title: "Are you sure?",
      text: `Do you want to ${nextStatus === "ACTIVE" ? "activate" : "deactivate"} this account?`,
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      updateTeacherStatus(
        { teacherId: teacher._id, status: nextStatus },
        {
          onSuccess: () => swal("Success", "Status updated successfully", "success"),
          onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
        }
      );
    });
  };

  const handleUpdatePassword = (values) => {
    if (values.newPassword && values.newPassword !== values.confirmPassword) {
      swal("Oops!", "Passwords do not match", "error");
      return;
    }

    swal({
      title: "Are you sure?",
      text: "Do you want to update the password?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      Post(
        TEACHERS.resetTeacherPassword + teacher._id,
        { password: values.newPassword || undefined },
        token
      )
        .then((response) => {
          if (response?.data?.status) {
            const generated = response?.data?.data?.password;
            const lines = ["Password updated successfully"];
            if (generated) lines.push(`Password: ${generated}`);
            swal("Success!", lines.join("\n"), "success");
            setResetOpen(false);
            form.resetFields();
          } else {
            swal("Oops!", response?.data?.message || response?.response?.data?.message || "Failed to update password", "error");
          }
        })
        .catch(() => {
          form.resetFields();
        });
    });
  };

  return (
    <div className="td-page">
      <div className="td-main">
      <article className="td-card">
        <DetailCoverHero
          coverKey={teacher?._id}
          backLabel="Back to teachers"
          onBack={() => navigate("/teachers")}
        />

        <div className="td-body">
          <div className="td-identity">
            {teacher?.image ? (
              <img
                className="td-avatar"
                src={`${UPLOADS_URL}/${teacher.image}`}
                alt={fullName}
              />
            ) : (
              <span className="td-avatar">{initials(teacher?.firstName, teacher?.lastName)}</span>
            )}
            <div className="td-identity-row">
              <h2>{fullName}</h2>
              <span className={`td-status is-${(teacher?.status || "inactive").toLowerCase()}`}>
                {statusLabel}
              </span>
            </div>
            <p>{role}</p>
          </div>

          <div className="td-chips">
            <span>
              <i className="is-orange">
                <MapPin size={14} />
              </i>
              {locationLabel || "Location not set"}
            </span>
            <span>
              <i className="is-orange">
                <Phone size={14} />
              </i>
              {teacher?.phone || "Phone not set"}
            </span>
            <span>
              <i className="is-orange">
                <Mail size={14} />
              </i>
              {teacher?.email || "Email not set"}
            </span>
          </div>

          <section className="td-section">
            <h3>About:</h3>
            {teacher?.bio ? (
              <p>{teacher.bio}</p>
            ) : (
              <div className="td-empty">
                <Inbox size={22} />
                <span>No about information yet</span>
              </div>
            )}
          </section>

          <section className="td-section">
            <h3>Education:</h3>
            {education.length ? (
              <ul>
                {education.map((item, index) => (
                  <li key={`${item.school}-${index}`}>
                    <strong>
                      {(item.subject || []).filter(Boolean).join(", ") || "Studies"}
                      {item.school ? `, ${item.school}` : ""}
                    </strong>
                    <em>{formatRange(item.start, item.end)}</em>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="td-empty">
                <Inbox size={22} />
                <span>No education records yet</span>
              </div>
            )}
          </section>

          <section className="td-section">
            <h3>Expertise:</h3>
            {expertise.length ? (
              <p>{expertise.join(", ")}</p>
            ) : (
              <div className="td-empty">
                <Inbox size={22} />
                <span>No expertise listed yet</span>
              </div>
            )}
          </section>
        </div>
      </article>

      <div className="td-actions">
        <button
          type="button"
          className="td-action is-primary"
          onClick={() => navigate(`/teachers/edit/${teacher._id}`)}
        >
          <Pencil size={15} />
          Edit details
        </button>
        <button
          type="button"
          className="td-action"
          onClick={() => navigate(`/teachers/attendance/${teacher._id}`)}
        >
          <Calendar size={15} />
          Attendance
        </button>
        <button
          type="button"
          className="td-action"
          onClick={() => setResetOpen(true)}
        >
          <KeyRound size={15} />
          Update password
        </button>
        <button
          type="button"
          className="td-action"
          disabled={isUpdatingStatus}
          onClick={handleStatus}
        >
          {isActive ? <UserX size={15} /> : <UserCheck size={15} />}
          {isActive ? "Deactivate" : "Activate"}
        </button>
        <button type="button" className="td-action is-danger" onClick={onDelete}>
          <Trash2 size={15} />
          Delete
        </button>
      </div>
      </div>

      <aside className="td-schedule">
        <div className="td-schedule-head">
          <h3>Timetable</h3>
          <p>
            {className ? `Class ${className}` : "No class assigned"}
            {" · "}
            {DAY_LABELS[selectedDay]}
          </p>
        </div>

        {classroomId ? (
          <div className="td-days">
            {WEEKDAYS.map((day) => (
              <button
                key={day.key}
                type="button"
                className={selectedDay === day.key ? "is-active" : ""}
                onClick={() => setSelectedDay(day.key)}
              >
                {day.label}
              </button>
            ))}
          </div>
        ) : null}

        {!classroomId ? (
          <div className="td-empty">
            <Inbox size={22} />
            <span>Assign a classroom to show this teacher's timetable</span>
          </div>
        ) : isLoading ? (
          <div className="td-empty">
            <span>Loading timetable…</span>
          </div>
        ) : slots.length ? (
          <div className="td-schedule-list">
            {slots.map((item, index) => (
              <article key={item._id || `${item.subject}-${index}`} className={`td-lesson ${LESSON_TONES[index % LESSON_TONES.length]}`}>
                <div className="td-lesson-copy">
                  <h4>{item.subject || "Period"}</h4>
                  <p>{item.description || className || "Classroom"}</p>
                  <div className="td-lesson-meta">
                    <span>
                      <i>
                        <Calendar size={13} />
                      </i>
                      {DAY_LABELS[item.day] || DAY_LABELS[selectedDay]}
                    </span>
                    <span>
                      <i>
                        <Clock size={13} />
                      </i>
                      {formatSlotTime(item.startTime, item.endTime)}
                    </span>
                  </div>
                </div>
                {teacher?.image ? (
                  <img className="td-lesson-avatar" src={`${UPLOADS_URL}/${teacher.image}`} alt="" />
                ) : (
                  <span className="td-lesson-avatar">{initials(teacher?.firstName, teacher?.lastName)}</span>
                )}
              </article>
            ))}
          </div>
        ) : (
          <div className="td-empty">
            <Inbox size={22} />
            <span>No periods on {DAY_LABELS[selectedDay]}</span>
          </div>
        )}
      </aside>

      <Modal
        title="Update Password"
        open={resetOpen}
        onCancel={() => {
          setResetOpen(false);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            danger
            onClick={() => {
              setResetOpen(false);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="generate" onClick={() => generateTeacherUpdatePassword(form)}>
            Generate
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            Update Password
          </Button>,
        ]}
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleUpdatePassword}>
          <Form.Item label="New Password" name="newPassword">
            <Input.Password size="large" placeholder="Leave blank to auto-generate" />
          </Form.Item>
          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const next = getFieldValue("newPassword");
                  if (!next && !value) return Promise.resolve();
                  if (!value) {
                    return Promise.reject(new Error("Please confirm the new password"));
                  }
                  if (next === value) return Promise.resolve();
                  return Promise.reject(new Error("Passwords do not match"));
                },
              }),
            ]}
          >
            <Input.Password size="large" placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
